import { fetchEventSource } from '@microsoft/fetch-event-source';
import { cmsApi } from 'common/api/cmsApi';
import { useStartSyncMutation } from 'common/api/endpoints/sync';
import { appTagList } from 'common/api/utils/apiTagTypes';
import { handleError } from 'common/api/utils/handleError';
import {
  EstimateErrorDetails,
  SyncNotification,
  SyncTaskNotification,
} from 'common/models/databaseSync/syncNotification';
import { DatabaseSyncStatus } from 'common/models/databaseSync/syncStatus';
import { environment } from 'environment';
import { useAuth } from 'features/auth/hooks';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  createBlankTasks,
  createIncompleteTasks,
  isSyncStart,
  updateCompletedTasksMessage,
  updateCurrentTasks,
} from '../components/Sub-views/SpokaneSyncView/utils/functions';
import { taskNames } from '../components/Sub-views/SpokaneSyncView/utils/taskNames';

type TaskResultsTableProps = {
  taskResults: SyncTaskNotification[];
  onSyncClick: () => void;
  waitingToStart: boolean;
  syncProgress: number;
  syncNotification: SyncNotification;
  syncStatusError: boolean;
  syncErrorMessage?: string;
  syncEstimateErrors?: EstimateErrorDetails[];
  startingLocally: boolean;
};

export const useSyncTableData = (): TaskResultsTableProps => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const [startSync, { isLoading: startingLocally, error: startSyncError }] =
    useStartSyncMutation();

  const [syncNotification, setSyncNotification] = useState<SyncNotification>({
    status: DatabaseSyncStatus.None,
  });
  const [completionNotifications, setCompletionNotifications] = useState<
    SyncTaskNotification[]
  >([]);
  const [taskResults, setTaskResults] = useState<SyncTaskNotification[]>([]);
  const [syncStatusError, setSyncStatusError] = useState(false);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string | undefined>(
    '',
  );
  const [waitingToStart, setWaitingToStart] = useState(false);
  const [joiningStoppedSync, setJoiningStoppedSync] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncEstimateErrors, setEstimateErrors] =
    useState<EstimateErrorDetails[]>();
  const isOpening = useRef(false);

  const onSyncClick = () => {
    // Note that resetting progress here only applies to the client
    // that's initiating the sync - other clients are reset by events
    // received from the server.
    setSyncProgress(0);
    setCompletionNotifications([]);
    startSync();
  };

  // This effect sets a waiting bar for the times when starting the sync is slow.
  // We do this after a delay to avoid bad flicker on quick starts.
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (startingLocally) {
      const timeout = setTimeout(() => setWaitingToStart(true), 1000);
      cleanup = () => clearTimeout(timeout);
    } else {
      setWaitingToStart(false);
    }

    return cleanup;
  }, [startingLocally]);

  useEffect(() => {
    if (startSyncError) {
      handleError(startSyncError, 'Error starting sync, please try again.');
    }
  }, [startSyncError]);

  useEffect(() => {
    const controller = new AbortController();
    const initEventSource = async () => {
      await fetchEventSource(
        new URL('data-sync/status', environment.apiRoute).toString(),
        {
          headers: { authorization: `Bearer ${auth.token}` },
          signal: controller.signal,
          onopen: async res => {
            isOpening.current = true;
            setSyncStatusError(!res.ok);
          },
          onerror: () => setSyncStatusError(true),
          onmessage: msg => {
            const parsedMsg = JSON.parse(msg.data) as SyncNotification;
            const starting = isSyncStart(parsedMsg);
            const joiningPendingSync =
              isOpening.current &&
              parsedMsg.status === DatabaseSyncStatus.Pending;
            const joiningStoppedSync =
              isOpening.current &&
              parsedMsg.status !== DatabaseSyncStatus.Pending;
            isOpening.current = false;

            setJoiningStoppedSync(joiningStoppedSync);
            setSyncNotification(parsedMsg);
            setCompletionNotifications(prev => {
              return starting
                ? []
                : updateCurrentTasks(joiningPendingSync ? [] : prev, parsedMsg);
            });
            setSyncErrorMessage(parsedMsg.error);
            setEstimateErrors(parsedMsg.estimateErrors);
          },
        },
      );
    };

    initEventSource();

    return () => controller.abort();
  }, [auth]);

  useEffect(() => {
    const taskFactor = 100 / (Object.keys(taskNames).length - 1);

    // When entering the view with no sync ongoing, we don't want to move the
    // progress bar to avoid the false impression of something happening.
    if (!joiningStoppedSync) {
      setSyncProgress(() =>
        isSyncStart(syncNotification)
          ? 0
          : completionNotifications.length * taskFactor,
      );
    }
  }, [joiningStoppedSync, syncNotification, completionNotifications]);

  useEffect(() => {
    if (
      syncStatusError ||
      (completionNotifications.length === 0 &&
        syncNotification.status !== DatabaseSyncStatus.Pending)
    ) {
      setTaskResults(createBlankTasks());
    } else {
      const syncResults = completionNotifications;
      const latestTaskDesc = syncResults[syncResults.length - 1]?.descriptor;
      const completedTasks = updateCompletedTasksMessage(syncResults);
      const incompleteTasks = createIncompleteTasks(latestTaskDesc);

      setTaskResults(completedTasks.concat(incompleteTasks));
    }
  }, [completionNotifications, dispatch, syncNotification, syncStatusError]);

  useEffect(() => {
    // Invalidate all caches when the user navigates away.
    return () => {
      dispatch(cmsApi.util.invalidateTags([...appTagList]));
    };
  }, [dispatch]);

  return {
    onSyncClick,
    startingLocally,
    waitingToStart,
    taskResults,
    syncNotification,
    syncProgress,
    syncStatusError,
    syncEstimateErrors,
    syncErrorMessage,
  };
};
